import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { z } from 'zod';
import { ensureOBSPresence } from '../utils/ensureOBSPresence';
import {
  Destination,
  DestinationMetadataApiClient,
  DestinationProviderApiClient,
  DestinationsStorageApiClient,
} from './DestinationsApiClient';
import { UnauthenticatedError } from '../utils/auth';
import { getBaseUrl, isLocalHost } from '../utils/isLocalHost';
import { AuthServer } from './AuthServer';
import { AbortedError, CreatorIneligibilityError, TransientError } from '../types/errors';

const knownTiktokErrors = z.object({
  error: z.object({
    code: z.string().refine((code) => code !== 'ok', { message: 'Code cannot be "ok"' }),
    http_status_code: z.number().optional(),
    log_id: z.string(),
    message: z.string(),
  }),
});

const tiktokCanGoLiveSchema = z.object({
  can_be_live: z.literal(true),
  reason: z.number(),
});

type TiktokAuthResponse = {
  access_token: string;
  expires_in: number;
  open_id: string;
  refresh_expires_in: number;
  refresh_token: string;
  scope: string;
  token_type: string;
};

export type TiktokAuthMetadata = {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  refreshExpiresAt: string;
};

export class TiktokApiClient implements DestinationProviderApiClient<'tiktok'> {
  static STREAM_SOURCE_NAME = 'SE.Live Streaming Source';
  static CLIENT_ID = isLocalHost() ? 'sbawsl0yzfk2xbsd2b' : 'awn102m5x3prf7j0';
  // This is ok to be here because it's a sandbox secret and in production will be replaced with the actual secret
  // And it wont be leaked
  static SECRET = 'rVefbJM8PXa905sadVNr25BFLWvBqlIQ';
  static baseURL = isLocalHost()
    ? window.location.origin + '/api/tiktok'
    : 'https://tiktok-api-proxy.streamelements.workers.dev';

  static REDIRECT_URI = isLocalHost()
    ? 'https://se-platform-login.streamelements.workers.dev/auth/tiktok/callback'
    : 'https://selive.streamelements.com/auth/tiktok/callback';

  static scopes = 'user.info.basic,live.room.tag,live.room.tag,live.room.info,live.room.manage' as const;
  private authContextProvider: AuthContextProvider;
  private serviceEnvironment: ServiceEnvironment;
  private tiktokAuthMetadata: TiktokAuthMetadata;

  constructor({
    tiktokAuthMetadata,
    authContextProvider,
    serviceEnvironment,
  }: {
    authContextProvider: AuthContextProvider;
    serviceEnvironment: ServiceEnvironment;
    tiktokAuthMetadata: TiktokAuthMetadata;
  }) {
    this.tiktokAuthMetadata = tiktokAuthMetadata;
    this.authContextProvider = authContextProvider;
    this.serviceEnvironment = serviceEnvironment;
  }

  static authServerPort = 845865;

  public static getManageBroadcastScopeAuthorizationUrl({ serverUrl }: { serverUrl: string }) {
    const url = new URL('https://www.tiktok.com/v2/auth/authorize/');
    const csrfState = btoa(JSON.stringify({ serverUrl }));

    url.searchParams.set('client_key', TiktokApiClient.CLIENT_ID);
    url.searchParams.set('scope', TiktokApiClient.scopes);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('state', csrfState);
    url.searchParams.set('redirect_uri', this.REDIRECT_URI);

    return {
      redirectUrl: url.toString(),
      csrfState,
    };
  }

  public static async exchangeCodeForAccessToken({ code }: { code: string }): Promise<TiktokAuthResponse> {
    const url = new URL(`${TiktokApiClient.baseURL}/v2/oauth/token/`);

    // const formData = new URLSearchParams();
    url.searchParams.set('client_key', TiktokApiClient.CLIENT_ID);
    url.searchParams.set('client_secret', this.SECRET);
    url.searchParams.set('code', decodeURIComponent(code));
    url.searchParams.set('grant_type', 'authorization_code');
    url.searchParams.set('redirect_uri', TiktokApiClient.REDIRECT_URI);

    const response = await fetch(url.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: url.searchParams.toString(),
      method: 'post',
    });

    const data = await response.json();

    if (!response.ok) {
      console.warn('exchangeCodeForAccessToken: invalid response from tiktok API: ', data);

      throw new TransientError('Invalid response from Tiktok API. Please try again later');
    }

    return data;
  }

  public static async refreshAccessToken({ refreshToken }: { refreshToken: string }): Promise<TiktokAuthResponse> {
    const url = new URL(`${TiktokApiClient.baseURL}/v2/oauth/token/`);

    const body = (() => {
      if (isLocalHost()) {
        const formData = new URLSearchParams();
        formData.set('client_key', TiktokApiClient.CLIENT_ID);
        formData.set('client_secret', TiktokApiClient.SECRET);
        formData.set('refresh_token', refreshToken);
        formData.set('grant_type', 'refresh_token');
        return formData.toString();
      }

      url.searchParams.set('client_key', TiktokApiClient.CLIENT_ID);
      url.searchParams.set('client_secret', TiktokApiClient.SECRET);
      url.searchParams.set('refresh_token', refreshToken);
      url.searchParams.set('grant_type', 'refresh_token');

      return null;
    })();

    const response = await fetch(url.toString(), {
      method: 'post',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    });

    const data = await response.json();

    if (!response.ok) {
      console.warn('refreshAccessToken: invalid response from tiktok API: ', data);

      throw new TransientError('Invalid response from Tiktok API. Please try again later');
    }

    return data;
  }

  public static async ensureFreshTokens(params: TiktokAuthMetadata) {
    let newAuthMetadata = params;

    if (!newAuthMetadata?.refreshExpiresAt || new Date(newAuthMetadata?.refreshExpiresAt) < new Date()) {
      const result = await this.connectTiktok();

      /**
       * Tiktok provides expiration time in seconds
       * https://developers.tiktok.com/doc/oauth-user-access-token-management#:~:text=The%20expiration%20of%20access_token%20in%20seconds.
       * */
      const expiresAtMiliseconds = result.expires_in * 1000;
      const refreshExpiresAtMiliseconds = result.refresh_expires_in * 1000;

      newAuthMetadata = {
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        expiresAt: new Date(Date.now() + expiresAtMiliseconds).toISOString(),
        refreshExpiresAt: new Date(Date.now() + refreshExpiresAtMiliseconds).toISOString(),
      };
    }

    if (new Date(newAuthMetadata.expiresAt) < new Date()) {
      const result = await this.refreshAccessToken({
        refreshToken: newAuthMetadata.refreshToken,
      });

      const expiresAtMiliseconds = result.expires_in * 1000;
      const refreshExpiresAtMiliseconds = result.refresh_expires_in * 1000;

      return {
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        expiresAt: new Date(Date.now() + expiresAtMiliseconds).toISOString(),
        refreshExpiresAt: new Date(Date.now() + refreshExpiresAtMiliseconds).toISOString(),
      };
    }

    return newAuthMetadata;
  }

  public static async passTiktokCallbackParams({
    serverUrl,
    ...params
  }: {
    code: string;
    state: string;
    serverUrl: string;
  }) {
    return await fetch(`${serverUrl}/auth/tiktok/callback`, {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {},
    });
  }

  public static getServerUrlFromState(state: string): string | null {
    try {
      const parsedState = JSON.parse(atob(state));
      return parsedState.serverUrl;
    } catch (err) {
      return null;
    }
  }

  public static async connectTiktok(): Promise<TiktokAuthResponse> {
    await ensureOBSPresence();

    const baseUrl = getBaseUrl();

    const tiktokAuthServer = new AuthServer({ provider: 'tiktok' });
    await tiktokAuthServer.listen();

    const { redirectUrl, csrfState } = this.getManageBroadcastScopeAuthorizationUrl({
      serverUrl: tiktokAuthServer.url,
    });

    // Build pre-auth URL that will request LNA permission before redirecting to OAuth
    const preAuthUrl = new URL('/auth/tiktok/pre-auth', baseUrl);
    preAuthUrl.searchParams.set('port', tiktokAuthServer.get().portNumber.toString());
    preAuthUrl.searchParams.set('authUrl', redirectUrl);

    let removeListener = () => {
      void 0;
    };

    const tiktokAuthPromise = new Promise<TiktokAuthResponse>((resolve, reject) => {
      removeListener = tiktokAuthServer.onAuthCallback(async (result) => {
        if (!result) {
          return reject(new AbortedError('Output connection aborted'));
        }

        const { code, state } = result;

        if (state !== csrfState) {
          return reject(new TransientError('CSRF state mismatch'));
        }

        if (!code) {
          return reject(new TransientError('Invalid code'));
        }

        const response = await TiktokApiClient.exchangeCodeForAccessToken({
          code,
        }).catch(() => null);

        if (!response?.access_token && !response?.refresh_token) {
          return reject(new TransientError('No access token or refresh token'));
        }

        if (!response.access_token) {
          return reject(new TransientError('No access token'));
        }

        return resolve(response);
      });
    });

    window.host.openDefaultBrowser(preAuthUrl.toString(), () => {
      console.log('Opened browser to pre-auth page');
    });

    const result = await tiktokAuthPromise;

    removeListener?.();

    console.log('Closing auth server');

    await tiktokAuthServer.dispose();

    return result;
  }

  private async makeRequest<T>(
    endpoint: string,
    { params, ...options }: RequestInit & { params?: Record<string, string> } = {}
  ): Promise<T> {
    const url = new URL(`${TiktokApiClient.baseURL}${endpoint}`);

    for (const [key, value] of Object.entries(params || {})) {
      url.searchParams.set(key, value);
    }

    const response = await fetch(url.toString(), {
      ...options,
      headers: {
        Authorization: `Bearer ${this.tiktokAuthMetadata.accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (response.status === 401) {
      throw new UnauthenticatedError('Unauthenticated');
    }

    const data = await response.json();
    const parsed = knownTiktokErrors.safeParse(data);

    if (parsed.success && parsed.data.error.code !== 'ok') {
      switch (parsed.data.error.code) {
        case 'user_has_no_live_auth':
          throw new CreatorIneligibilityError('User is ineligible for live streaming: ' + parsed.data.error.message);
        default:
          throw new Error(parsed.data.error.message + '('+ parsed.data.error.code + ')');
      }
    }

    return data as T;
  }

  async getUserInfo() {
    const data = await this.makeRequest<{
      data: {
        user: {
          open_id: string;
          display_name: string;
        };
      };
    }>('/v2/user/info/', {
      params: {
        fields: 'open_id,display_name',
      },
    });

    return data.data.user;
  }

  async fetchCategories() {
    const data = await this.makeRequest<{
      data: {
        game_tag_list: {
          full_name: string;
          game_mask_id: string;
        }[];
      };
      error?: {
        code: string;
        http_status_code: number;
        log_id: string;
        message: string;
      };
    }>('/v2/live/room/tag/list/', {
      params: {
        fields: 'game_tag_list',
      },
    });

    if ((data?.error?.http_status_code ?? 200) >= 400) {
      throw new Error(data?.error?.message ?? 'Unknown error occurred when fetching streaming categories.');
    }

    return data.data.game_tag_list;
  }

  async searchCategory(query: string) {
    const data = await this.makeRequest<{
      data: {
        is_end: boolean;
        game_tag_list: {
          full_name: string;
          game_mask_id: string;
        }[];
      };
      error?: {
        code: string;
        http_status_code: number;
        log_id: string;
        message: string;
      };
    }>('/v2/live/room/tag/game/search/', {
      params: {
        fields: ['is_end', 'game_tag_list'].join(','),
      },
      body: JSON.stringify({
        query_string: query,
        limit: 10,
      }),
    });

    if ((data?.error?.http_status_code ?? 200) >= 400) {
      throw new Error(data?.error?.message ?? 'Unknown error occurred when searching for streaming categories.');
    }

    return data.data.game_tag_list;
  }

  async checkUserCanStream({ providerId }: { providerId: string }) {
    const { token } = await this.authContextProvider.getAuthContext();
    const url = new URL(`${this.serviceEnvironment.kappaV3ApiBaseUrl}/tiktok`);

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        token: this.tiktokAuthMetadata.accessToken,
        providerId,
      }).toString(),
    });

    // if (response.statusCode === 401) {
    if (response.status === 401) {
      throw new UnauthenticatedError('Unauthenticated');
    }

    const data = (await response.json()) as {
      can_be_live: boolean;
      reason: number;
    };

    const parsed = tiktokCanGoLiveSchema.safeParse(data);

    if (!parsed.success) {
      throw new CreatorIneligibilityError('User is ineligible for live streaming');
    }

    return true;
  }

  async createRoom({ title, categoryId }: { title: string; categoryId: string }) {
    const response = await this.makeRequest<{
      data: {
        stream_url: {
          name: string;
          push_url: string;
          push_key: string;
          origin_flv_pull_url: string;
          origin_hls_pull_url: string;
        };
      };
      error?: {
        code: string;
        http_status_code: number;
        log_id: string;
        message: string;
      };
    }>('/v2/live/room/create/', {
      method: 'POST',
      params: {
        fields: [
          'stream_url.name',
          'stream_url.push_url',
          'stream_url.push_key',
          'stream_url.origin_flv_pull_url',
          'stream_url.origin_hls_pull_url',
        ].join(','),
      },
      body: JSON.stringify({
        title,
        device_platform: 'windows',
        game_mask_id: categoryId,
      }),
    });

    const { push_key: streamKey, push_url: serverUrl, name: broadcastId } = response.data.stream_url;

    return {
      streamKey,
      serverUrl,
      broadcastId,
    };
  }

  public async endBroadcast({ broadcastId } : { broadcastId?: string }) {
    if (!broadcastId) {
      throw new Error('Broadcast ID is required');
    }

    try {
      const response = await this.makeRequest<{
        error?: {
          code: string;
          http_status_code: number;
          log_id: string;
          message: string;
        };
      }>('/v2/live/room/finish/', {
        method: 'POST',
        body: JSON.stringify({
          reason: 2,
          stream_name: broadcastId,
        }),
      });

      if (response.error && response.error.code !== 'ok') {
        console.log('Tiktok: trying to finish a broadcast failed: ', response.error);
      }
    } catch {
      // We don't care if it fails. We want the code to continue flowing
      // as if this was a success anyway.
      //
      // The worst thing which can happen, is that Tiktok will wait until
      // the live broadcast times out.
    }
  }

  async createStreamingOutput(options: { notifyOBS?: boolean } = {}) {
    const destinationsStorageApiClient = new DestinationsStorageApiClient({
      authContextProvider: this.authContextProvider,
      serviceEnvironment: this.serviceEnvironment,
    });

    const userInfo = await this.getUserInfo();

    if (!userInfo) {
      throw new Error('Failed to get user info');
    }

    let canGoLiveError: CreatorIneligibilityError | undefined;

    try {
      await this.checkUserCanStream({ providerId: userInfo.open_id });
    } catch (error) {
      if (error instanceof CreatorIneligibilityError) {
        canGoLiveError = error;
      } else {
        throw error;
      }
    }

    const destinationMetadataApiClient = new DestinationMetadataApiClient({
      authContextProvider: this.authContextProvider,
      serviceEnvironment: this.serviceEnvironment,
      destinationMetadata: {
        id: userInfo.open_id,
        provider: 'tiktok',
        providerId: userInfo.open_id,
        displayName: userInfo.display_name,
        accessToken: this.tiktokAuthMetadata.accessToken,
        refreshToken: this.tiktokAuthMetadata.refreshToken,
        expiresAt: new Date(this.tiktokAuthMetadata.expiresAt).toISOString(),
        refreshExpiresAt: new Date(this.tiktokAuthMetadata.refreshExpiresAt).toISOString(),
        error: canGoLiveError ? ({ message: canGoLiveError.message } as Error) : undefined,
        outputs: [
          {
            id: `${userInfo.open_id}-tiktok-1`,
            name: userInfo.display_name,
            isEnabled: !canGoLiveError,
            streamingSettings: {
              serverUrl: '',
              streamKey: '',
            },
            auxiliaryData: {
              destinationId: userInfo.open_id,
              isPrimaryOutput: false,
            },
            videoCompositionId: 'default',
            audioCompositionId: 'default',
          },
        ],
      },
    });

    await destinationsStorageApiClient
      .storeDestination(
        {
          destination: destinationMetadataApiClient.get(),
        },
        options
      )
      .then((r) => {
        return r;
      });

    return {
      destinationMetadata: destinationMetadataApiClient.get(),
      error: canGoLiveError,
    };
  }

  public setupStreamRoom: DestinationProviderApiClient<'tiktok'>['setupStreamRoom'] = async ({
    title,
    categoryId,
    destinationMetadata,
    outputId,
  }) => {
    const { streamKey, serverUrl, broadcastId } = await this.createRoom({ title, categoryId });

    const destinationMetadataApiClient = new DestinationMetadataApiClient({
      authContextProvider: this.authContextProvider,
      serviceEnvironment: this.serviceEnvironment,
      destinationMetadata,
    });

    const result = destinationMetadataApiClient.updateDestination({
      outputs: destinationMetadata.outputs.map((output) => {
        if (output.id !== outputId) {
          return output;
        }

        return {
          ...output,
          streamingSettings: {
            serverUrl,
            streamKey,
          },
          auxiliaryData: {
            ...output.auxiliaryData,
            streamTitle: title,
            streamCategory: categoryId,
            broadcastId,
          },
        } as Destination['outputs'][number];
      }),
    });

    return result;
  };

  async updateStreamRoom(params: {
    outputId: string;
    title?: string;
    categoryId?: string;
    privacyStatus?: string;
  }): Promise<void> {
    void params;
  }

  public async getCategories({ query }: { query: string }): Promise<Map<string, string>> {
    const response = await this.makeRequest<{
      data: {
        game_tag_list: {
          full_name: string;
          game_mask_id: string;
        }[];
      };
    }>('/v2/live/room/tag/game/search/', {
      method: 'POST',
      params: {
        fields: 'game_tag_list',
      },
      body: JSON.stringify({
        query_string: query,
        limit: '10',
      }),
    });

    return new Map(response.data.game_tag_list.map((category) => [category.game_mask_id, category.full_name]));
  }

  public async getCategoryById(params: { categoryId: string }): Promise<{ id: string; displayName: string } | null> {
    void params;
    return null;
  }
}
